import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSchoolData } from './SchoolDataProvider';
import { addDaysIso } from './calendarMath';

const STREAK_LOOKBACK_DAYS = 30;

// Consecutive school days (going backward from yesterday — today isn't
// finished yet) where every must-do item was completed. Days with no
// must-do items scheduled (weekends, days off) don't break the streak, they
// just don't count either way (gap-review item 9 — lightweight gamification
// on data StatusBanner already has, no new writes).
function computeStreak(assignments, today) {
  const byDate = {};
  for (const a of assignments) if (a.isFloor && a.date) (byDate[a.date] ||= []).push(a);

  let streak = 0;
  let date = addDaysIso(today, -1);
  for (let i = 0; i < STREAK_LOOKBACK_DAYS; i++) {
    const items = byDate[date];
    if (items?.length) {
      if (items.every(a => a.status === 'complete')) streak++;
      else break;
    }
    date = addDaysIso(date, -1);
  }
  return streak;
}

const TONE_COLOR = { good: 'var(--good)', neutral: 'var(--ink-2)', warn: 'var(--bad)' };

// Encouraging, game-like status strip — "10 left this week" / "3 need a day" /
// "2 from earlier days still open" / a caught-up celebration.
//
// Split in two so the owner's Board tab can render the same numbers: StatusChips
// is pure and router-free, StatusBanner is the routed wrapper that makes the
// chips tappable. Without an onNavigate they render as plain text.
export function StatusChips({ onNavigate }) {
  const { assignments, today, weekNumber, unplacedThisWeek, overdue, flagged, loading, seeded } = useSchoolData();

  if (loading || !seeded) return null;

  const thisWeek = assignments.filter(a => a.weekNumber === weekNumber);
  // Scoped to work that HAS a day — the "need a day" chip below owns the rest,
  // and counting a tile in both would double-report it.
  const leftThisWeek = thisWeek.filter(a => a.date && a.status !== 'complete').length;
  const needsDay = unplacedThisWeek.length;
  const streak = computeStreak(assignments, today);

  const chips = [];
  if (streak >= 2) {
    chips.push({ text: `🔥 ${streak}-day streak`, tone: 'good' });
  }
  // Only a celebration if there is genuinely nothing left — including tiles
  // with no day, which would otherwise be silently excluded by the scoping above.
  if (leftThisWeek === 0 && needsDay === 0 && thisWeek.length > 0) {
    chips.push({ text: '🎉 All caught up this week!', tone: 'good' });
  } else if (leftThisWeek > 0) {
    chips.push({ text: `${leftThisWeek} left this week`, tone: 'neutral', to: '/school/week' });
  }
  if (needsDay > 0) {
    chips.push({ text: `📌 ${needsDay} need${needsDay === 1 ? 's' : ''} a day`, tone: 'warn', to: '/school/week' });
  }
  if (overdue.length > 0) {
    chips.push({ text: `${overdue.length} from earlier day${overdue.length === 1 ? '' : 's'} still open`, tone: 'warn', to: '/school/week' });
  }
  if (flagged.length > 0) {
    chips.push({ text: `${flagged.length} flagged`, tone: 'warn', to: '/school/flagged' });
  }

  if (chips.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '10px 0 2px' }}>
      {chips.map(c => {
        const style = {
          fontFamily: 'var(--font-mono)', fontSize: 12, padding: '4px 10px',
          borderRadius: 999, border: '1px solid var(--line)', background: 'var(--card)',
          color: TONE_COLOR[c.tone],
        };
        return c.to && onNavigate ? (
          <button key={c.text} type="button" style={{ ...style, cursor: 'pointer' }} onClick={() => onNavigate(c.to)}>
            {c.text}
          </button>
        ) : (
          <span key={c.text} style={style}>{c.text}</span>
        );
      })}
    </div>
  );
}

export default function StatusBanner() {
  const navigate = useNavigate();
  return <StatusChips onNavigate={navigate} />;
}
